import { type RefObject, type SyntheticEvent, useEffect, useState } from "react";
import { compareAddCar } from "../../../common-deprecated/features/compare/redux/actions/NewCarCompareExternalDataActions";
import { type CompareV2DispatchType, useCompareV2Selector } from "../redux/store";
import type { ModelGradeBodyTypeIdsType } from "../../../common-deprecated/features/compare/utils/newCarCompareTypes";
import { emptySelectedCarIds } from "../utils/constants";
import { trackCompareGradeSelect } from "../utils/tracking";
import { useDispatch } from "../../../shared-logic/redux/store";

type HookReturnType = {
    selectedCar: ModelGradeBodyTypeIdsType;
    setSelectedCar: (state: ModelGradeBodyTypeIdsType) => void;
    makeSelection: (event: SyntheticEvent) => void;
    closeFunction: (event?: SyntheticEvent) => void;
    compareHeaderHeight: number | null;
    viewPortHeight: number | null;
};

const useSelectGrade = (
    index: number,
    opened: boolean | undefined,
    setOpenState: (open: boolean) => void,
    compareHeaderRef: RefObject<HTMLElement | null>,
): HookReturnType => {
    const dispatch = useDispatch<CompareV2DispatchType>();
    const available = useCompareV2Selector(({ newCarCompare }) => newCarCompare.available);
    const prevSelectedCarAtIndex = useCompareV2Selector(({ newCarCompare }) => newCarCompare.grades[index]);

    // selectedCar is used to store the partial model / grade / body selection, which is then later dispatched once done
    const [selectedCar, setSelectedCar] = useState<ModelGradeBodyTypeIdsType>({ ...emptySelectedCarIds });
    const [compareHeaderHeight, setCompareHeaderHeight] = useState<number | null>(null);
    const [viewPortHeight, setViewPortHeight] = useState<number | null>(null);

    // Click on a model, grade and body combo
    const makeSelection = (event?: SyntheticEvent): void => {
        if (event) event.stopPropagation();
        if ((selectedCar.model && selectedCar.gradeId && selectedCar.bodyTypeId) || selectedCar.configurationId) {
            // Tracking
            const availableModel = available.find(
                (model) => model.modelId === selectedCar.model || model.modelCode === selectedCar.model,
            );
            const availableSubmodel = availableModel?.subModels?.find(
                (submodel) => submodel.subModelId === selectedCar.submodelId,
            );
            const availableGrade = availableSubmodel?.grades?.find((grade) => grade.gradeId === selectedCar.gradeId);
            const availableBodyType = availableGrade?.bodyTypes?.find(
                (bodytype) => bodytype.bodyTypeId === selectedCar.bodyTypeId,
            );
            const trackingCarNameObject = {
                modelName: availableModel?.modelName || "",
                gradeName: availableGrade?.gradeName || "",
                bodyTypeName: availableBodyType?.bodyTypeName || "",
                configurationId: selectedCar.configurationId,
            };
            trackCompareGradeSelect(prevSelectedCarAtIndex ? "modify" : "add", trackingCarNameObject);

            // Update view
            setOpenState(false);
            dispatch(compareAddCar(selectedCar, null, index));
            setSelectedCar({ ...emptySelectedCarIds });
        }
    };

    const closeFunction = (event?: SyntheticEvent): void => {
        if (event) event.stopPropagation();
        setOpenState(false);
        setSelectedCar({ ...emptySelectedCarIds });
    };

    useEffect(() => {
        const update = (): void => {
            if (compareHeaderRef.current) {
                const { height } = compareHeaderRef.current.getBoundingClientRect();
                setCompareHeaderHeight(height);
                setViewPortHeight(window.innerHeight);
            }
        };

        update();
        window.addEventListener("resize", update);
        return () => window.removeEventListener("resize", update);
    }, [compareHeaderRef, opened]);

    return { selectedCar, setSelectedCar, makeSelection, closeFunction, compareHeaderHeight, viewPortHeight };
};

export default useSelectGrade;
